import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, CircularProgress, Grid, InputLabel, Typography } from '@mui/material';
import { VolunteerActivismOutlined, AccessTimeOutlined } from '@mui/icons-material';
import moment from 'moment';

import Modal from 'components/Modal/Modal';
import { showErrorAction } from 'containers/Notification/actions';
import { selectUser } from 'entities/user/selectors';
import { formatFullName } from 'utils/formatFullName';
import {
  ExtendedLocationObjectInput,
  GooderaBookOpportunityPayload,
  GooderaCountry,
  GooderaLanguage,
} from 'generated/graphql';
import { gray3, lightGray } from 'utils/color-styles';
import { controlledTextField } from 'components/ReactHookForm/controlledTextField';
import { TimeZonePicker } from 'components/TimeZone/TimeZonePicker';
import getDeedTypeForDisplay from 'utils/getDeedTypeForDisplay';
import { selectLocalSetting } from 'localSettings/selectors';
import PersonWithHeart from 'assets/person-with-heart.svg';
import Img from 'retired/Img';
import { DateWithStartAndEndTime } from 'components/DateWithStartAndEndTime';
import FormLocationField from 'retired/FormLocationField';
import addressToLocation from 'utils/addressToLocation';

import { Opportunity } from '../types';
import { ProviderLogo } from '../ProviderLogo';

import { GooderaLanguagePicker } from './GooderaLanguagePicker/GooderaLanguagePicker';
import { GooderaCountryPicker } from './GooderaCountryPicker/GooderaCountryPicker';

const NameInput = controlledTextField<GooderaBookOpportunityPayload>()('name', {
  rules: { required: true },
});
const EmailInput = controlledTextField<GooderaBookOpportunityPayload>()('email', {
  rules: { required: true },
});
const ExpectedVolunteerCountInput = controlledTextField<GooderaBookOpportunityPayload>()(
  'expectedVolunteerCount',
  {
    rules: {
      required: 'Cannot be empty',
      min: { value: 1, message: 'Cannot be empty' },
    },
  }
);

export const BookGooderaOpportunityModal = ({
  opportunity,
  onClose,
  importOpportunity,
}: {
  opportunity: Opportunity;
  onClose: () => void;
  importOpportunity: (values: GooderaBookOpportunityPayload) => Promise<void>;
}) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const timeZone = useSelector((state) => selectLocalSetting(state, 'timeZone'));
  const viewerCountry = useSelector((state) =>
    selectLocalSetting(state, 'viewerCountry')
  ) as GooderaCountry;
  const name = formatFullName(user?.fullName?.toJS());

  const [extendedLocation, setExtendedLocation] = useState<
    ExtendedLocationObjectInput | undefined
  >();

  const minimumWeeksInAdvance = opportunity?.virtual ? 1 : 2;
  const durationInMinutes = opportunity?.duration ?? 60;
  const durationInHours = Math.ceil(durationInMinutes / 60);
  const oneWeekInDays = 7;
  const defaultStartingHour = 12;
  const defaultEndHour = defaultStartingHour + durationInHours;
  const minimumDaysInAdvance = minimumWeeksInAdvance * oneWeekInDays;
  const startingAt = new Date();
  startingAt.setDate(startingAt.getDate() + minimumDaysInAdvance);
  startingAt.setHours(defaultStartingHour);
  startingAt.setMinutes(0);
  startingAt.setSeconds(0);

  const durationHumanized = `${durationInHours} ${durationInHours === 1 ? 'hour' : 'hours'}`;

  const endingAt = new Date();
  endingAt.setDate(endingAt.getDate() + minimumDaysInAdvance);
  endingAt.setHours(defaultEndHour);
  endingAt.setMinutes(0);
  endingAt.setSeconds(0);

  type Payload = GooderaBookOpportunityPayload;

  const {
    handleSubmit: reactHookFormHandleSubmit,
    formState: { isSubmitting },
    control,
  } = useForm<Payload>({
    mode: 'onChange',
    defaultValues: {
      startingAt,
      endingAt,
      name,
      email: user?.email,
      timezone: timeZone ?? 'America/New_York',
      country: viewerCountry ?? GooderaCountry.Us,
      language: GooderaLanguage.Eng,
    },
  });

  const handleSubmit = reactHookFormHandleSubmit(async (values) => {
    if (!extendedLocation && !opportunity.virtual) {
      dispatch(showErrorAction('Please choose a valid location'));
      return;
    }

    // @ts-expect-error address is a field we only need in order to generate the eventLocation object.
    // We don't want to include it in the mutation
    const { address, ...restValues } = values;

    try {
      await importOpportunity({
        ...restValues,
        extendedLocationObject: extendedLocation,
        expectedVolunteerCount: Number(values.expectedVolunteerCount),
      });
      onClose();
    } catch (e) {
      dispatch(showErrorAction('There was an error booking this opportunity'));
    }
  });

  const eventTypeLabel = opportunity?.virtual ? 'Virtual' : 'In-Person';

  const details = (
    <>
      <Box>
        <Typography fontSize={14} fontWeight={400} color={gray3} mb={1}>
          {getDeedTypeForDisplay(opportunity.type as string)}
        </Typography>
        <Typography fontSize={24} fontWeight={500} lineHeight={1.16}>
          {opportunity.name}
        </Typography>
        {durationInHours && (
          <Box display="flex" mt={3}>
            <Box>
              <AccessTimeOutlined color={gray3} />
            </Box>
            <Box ml={1}>
              <Typography mb={1} fontSize={14} fontWeight={400} color={gray3}>
                Duration
              </Typography>
              <Typography fontSize={14} fontWeight={400}>
                {durationHumanized}
              </Typography>
            </Box>
          </Box>
        )}
        <Box display="flex" mt={3}>
          <Box>
            <VolunteerActivismOutlined color={gray3} />
          </Box>
          <Box ml={1}>
            <Typography mb={1} fontSize={14} fontWeight={400} color={gray3}>
              Event Type
            </Typography>
            <Typography fontSize={14} fontWeight={400}>
              {eventTypeLabel}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box display="flex" alignItems="flex-end">
        <Img src={PersonWithHeart} width={91} />
        <ProviderLogo style={{ margin: 0 }} provider={opportunity.provider} />
      </Box>
    </>
  );

  const form = (
    <form onSubmit={handleSubmit} disabled={isSubmitting}>
      <Box px={2}>
        <Typography mb={2} fontSize={20} fontWeight={500}>
          Enter Event Details
        </Typography>
        <InputLabel required shrink>
          Event date, start and end time
        </InputLabel>
        <Controller
          control={control}
          name="startingAt"
          rules={{ required: 'Event start time is required' }}
          render={(startingAtProp) => (
            <Controller
              control={control}
              name="endingAt"
              rules={{ required: 'Event end time is required' }}
              render={(endingAtProp) => (
                <DateWithStartAndEndTime
                  startingAtInput={{
                    value: startingAtProp.field.value,
                    onChange: startingAtProp.field.onChange,
                  }}
                  endingAtInput={{
                    value: endingAtProp.field.value,
                    onChange: endingAtProp.field.onChange,
                  }}
                  error={Boolean(startingAtProp.fieldState.error || endingAtProp.fieldState.error)}
                  hideDuration
                  isPreviousDateDisabled
                  minDate={moment.utc().add(minimumWeeksInAdvance, 'weeks').endOf('day').toDate()}
                />
              )}
            />
          )}
        />
        <Typography mt={1} fontSize={10} fontWeight={400} color={gray3} fontStyle="oblique">
          A minimum of {minimumWeeksInAdvance} week(s) is required for {eventTypeLabel} events.
        </Typography>
        <Box mt={2}>
          <Controller
            control={control}
            name="timezone"
            rules={{ required: { message: 'This field is required', value: true } }}
            render={({ field, fieldState }) => (
              <TimeZonePicker
                required
                timeZone={field.value || ''}
                label="Timezone"
                setTimeZone={field.onChange}
                fullWidth
                error={fieldState.error?.message}
              />
            )}
          />
        </Box>

        <Box mt={2}>
          <NameInput
            controllerProps={{ control }}
            fullWidth
            label="Point of contact name"
            variant="standard"
          />
        </Box>

        <Box mt={2}>
          <EmailInput
            controllerProps={{ control }}
            fullWidth
            label="Point of contact email address"
            variant="standard"
          />
        </Box>

        <Box mt={2}>
          <Controller
            control={control}
            name="country"
            rules={{ required: { message: 'This field is required', value: true } }}
            render={({ field, fieldState }) => (
              <GooderaCountryPicker
                required
                value={field.value || ''}
                onChange={field.onChange}
                fullWidth
                label="Country"
                error={fieldState.error?.message}
              />
            )}
          />
        </Box>
        {!opportunity.virtual && (
          <Box mt={2}>
            <InputLabel required shrink>
              Location Address
            </InputLabel>
            <Controller
              control={control}
              // @ts-expect-error address is a field we only need in order to generate the eventLocation object.
              name="address"
              render={({ field }) => (
                <div style={{ width: '100%' }}>
                  <FormLocationField
                    input={{
                      onChange: (value) => {
                        field.onChange(value);
                        setExtendedLocation(undefined);
                      },
                      value: field.value,
                      inputStyle: { width: '100%', padding: 0 },
                    }}
                    placeholder="Address"
                    meta={{}}
                    afterSelect={(value) => {
                      addressToLocation(value).subscribe(({ locationObject, location, meta }) => {
                        setExtendedLocation({
                          address: locationObject.address,
                          area: locationObject.area,
                          city: locationObject.city,
                          country: locationObject.country,
                          countryCode: locationObject.countryCode,
                          geo: {
                            type: location.type,
                            coordinates: location.coordinates,
                          },
                          postalCode: locationObject.postalCode,
                          state: locationObject.state,
                          stateCode: locationObject.stateCode,
                          placeId: meta.placeId,
                          url: meta.googleMapsUrl,
                        });
                      });
                    }}
                  />
                </div>
              )}
            />
          </Box>
        )}

        <Box mt={2}>
          <Controller
            control={control}
            name="language"
            rules={{ required: { message: 'This field is required', value: true } }}
            render={({ field, fieldState }) => (
              <GooderaLanguagePicker
                required
                value={field.value || ''}
                onChange={field.onChange}
                fullWidth
                label="Preferred language"
                error={fieldState.error?.message}
              />
            )}
          />
        </Box>

        <Box mt={2}>
          <ExpectedVolunteerCountInput
            controllerProps={{ control }}
            variant="standard"
            fullWidth
            type="number"
            label="Number of participants"
          />
        </Box>
      </Box>

      <Box mt={4} display="flex" justifyContent="flex-end">
        <Button type="button" variant="contained" disabled={isSubmitting} onClick={handleSubmit}>
          {isSubmitting ? <CircularProgress size={20} /> : 'Book'}
        </Button>
      </Box>
    </form>
  );

  return (
    <Modal
      style={{
        // @NOTE-DP: we want the date selector dropdown to be above it
        zIndex: 1040,
      }}
      open
      fullWidth
      maxWidth="md"
      onClose={onClose}
      width={850}
    >
      <Grid container spacing={0}>
        <Grid item xs={4} borderRight={`1px solid ${lightGray}`}>
          <Box
            p={4}
            display="flex"
            justifyContent="space-between"
            flexDirection="column"
            height="100%"
          >
            {details}
          </Box>
        </Grid>
        <Grid item xs={8}>
          <Box p={4}>{form}</Box>
        </Grid>
      </Grid>
    </Modal>
  );
};
